import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { WorkspaceProvider, useWorkspace } from './contexts/WorkspaceContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from 'sonner';
import AppShell from './components/layout/AppShell';
import ClientPortal from './components/layout/ClientPortal';
import TaskManagerShell from './components/layout/TaskManagerShell';
import LoginPage from './pages/LoginPage';
import AppSelectorPage from './pages/AppSelectorPage';
import DashboardPage from './pages/DashboardPage';
import MediaLibraryPage from './pages/MediaLibraryPage';
import ContentCalendarPage from './pages/ContentCalendarPage';
import PostComposerPage from './pages/PostComposerPage';
import SocialQueuePage from './pages/SocialQueuePage';
import ClientSocialTimelinePage from './pages/ClientSocialTimelinePage';
import ClientContentCalendarPage from './pages/ClientContentCalendarPage';
import ApprovalsPage from './pages/ApprovalsPage';
import IntegrationsPage from './pages/IntegrationsPage';
import AnalyticsPage from './pages/AnalyticsPage';
import AdManagerPage from './pages/AdManagerPage';
import AIAssistantPage from './pages/AIAssistantPage';
import TeamPage from './pages/TeamPage';
import SettingsPage from './pages/SettingsPage';
import UserSettingsPage from './pages/UserSettingsPage';
import ClientDashboardPage from './pages/ClientDashboardPage';
import ReportsPage from './pages/ReportsPage';
import ClientGroupsPage from './pages/ClientGroupsPage';
import SharedProjectPage from './pages/SharedProjectPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import UnsubscribePage from './pages/UnsubscribePage';
import NotFoundPage from './pages/NotFoundPage';
import TaskDashboardPage from './pages/TaskDashboardPage';
import TaskListPage from './pages/TaskListPage';
import TaskCalendarPage from './pages/TaskCalendarPage';
import TimeTrackingPage from './pages/TimeTrackingPage';
import PagesPage from './pages/PagesPage';
import MyTasksPage from './pages/MyTasksPage';
import ProjectsPage from './pages/ProjectsPage';
import NotificationsPage from './pages/NotificationsPage';
import InstallGuidePage from './pages/InstallGuidePage';
import KanbanPage from './pages/KanbanPage';
import PermissionsPage from './pages/PermissionsPage';
import TaskAIPage from './pages/TaskAIPage';
import NotesPage from './pages/NotesPage';
import MeetingNotesPage from './pages/MeetingNotesPage';
import TeamViewPage from './pages/TeamViewPage';
import TeamTasksPage from './pages/TeamTasksPage';
import TeamReportPage from './pages/TeamReportPage';
import TeamAvailabilityPage from './pages/TeamAvailabilityPage';
import AttendancePage from './pages/AttendancePage';
import AttendanceV2Page from './pages/AttendanceV2Page';
import PayrollPage from './pages/PayrollPage';
import StellarLoadingScreen from './components/StellarLoadingScreen';
import ErrorBoundary from './components/ErrorBoundary';
import MyClientsPage from './pages/MyClientsPage';
import PublicContentReviewPage from './pages/PublicContentReviewPage';
import PublicCalendarReviewPage from './pages/PublicCalendarReviewPage';
import IntegrationsHubPage from './pages/IntegrationsHubPage';
import TeamChatPage from './pages/TeamChatPage';
import AIChatPage from './pages/AIChatPage';
import NotionImportPage from './pages/NotionImportPage';
import UnifiedTasksPage from './pages/UnifiedTasksPage';
import AcceptInvitePage from './pages/AcceptInvitePage';
// WeeklyStandupPage is now mounted inside TeamReportPage as the "Standup" tab.
import AIAgentsPage from './pages/AIAgentsPage';
import UserActivityPage from './pages/UserActivityPage';
import ContractsPage from './pages/ContractsPage';
import ContractTemplatesPage from './pages/ContractTemplatesPage';
import EodReportsPage from './pages/EodReportsPage';
import PersonDetailPage from './pages/PersonDetailPage';
import ReviewsPage from './pages/ReviewsPage';
import IdeaBankPage from './pages/IdeaBankPage';
import { SWUpdateToast } from './components/SWUpdateToast';
import '@/App.css';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <img src="/stp-logo.png" alt="Stellar" className="h-12 w-12 object-contain animate-pulse" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// Iter 69 — Owner-only route guard (Contracts module).
// Server-side enforcement is the real gate; this just blocks the navigation
// for non-Owners so they don't see a flash of the page before the API 403s.
//
// Iter 71 (2026-05-18) — unified guard UX. All role guards now:
//   1. Surface a non-blocking toast on block ("Access denied — ...")
//   2. Redirect to Dashboard (/tasks)
//   3. console.debug the block attempt for dev visibility
// Mirrors the new AdminOrOwnerRoute / ManagerOrAboveRoute HOCs below.
function useRoleGuard(label) {
  const { user } = useAuth();
  const { workspaceRole, loading } = useWorkspace();
  return { user, workspaceRole, loading, label };
}

function GuardedRedirect({ label, requiredRole }) {
  // eslint-disable-next-line no-console
  console.debug(`[role-guard] blocked — required: ${requiredRole}, label: ${label}`);
  // Lazy toast — sonner is mounted at root; importing here triggers no cycles.
  const _toast = require('sonner').toast;
  _toast.error('Access denied — this section is restricted.', {
    description: requiredRole ? `Required role: ${requiredRole}.` : undefined,
    id: `role-guard-${label}`,
  });
  return <Navigate to="/tasks" replace />;
}

function OwnerOnlyRoute({ children }) {
  const { user, workspaceRole, loading } = useRoleGuard('owner-only');
  if (loading) {
    return (
      <div className="p-12 text-center text-sm text-muted-foreground">Loading…</div>
    );
  }
  const isOwner = user?.role === 'super_admin' || workspaceRole === 'owner';
  if (!isOwner) return <GuardedRedirect label="owner-only" requiredRole="Owner" />;
  return children;
}

// Iter 71 — Admin/Owner route guard. Wraps Automations, Integrations,
// workspace settings and members tabs. Server endpoints MUST also 403
// for non-permitted roles regardless of this front-end gate.
function AdminOrOwnerRoute({ children }) {
  const { user, workspaceRole, loading } = useRoleGuard('admin-or-owner');
  if (loading) {
    return <div className="p-12 text-center text-sm text-muted-foreground">Loading…</div>;
  }
  const isAdminOrOwner =
    user?.role === 'super_admin' ||
    workspaceRole === 'owner' ||
    workspaceRole === 'admin';
  if (!isAdminOrOwner) {
    return <GuardedRedirect label="admin-or-owner" requiredRole="Admin or Owner" />;
  }
  return children;
}

// Iter 71 — Manager/Admin/Owner route guard. Reserved for management-level
// pages (e.g. team-wide reports, hierarchy views).
function ManagerOrAboveRoute({ children }) {
  const { user, workspaceRole, loading } = useRoleGuard('manager-or-above');
  if (loading) {
    return <div className="p-12 text-center text-sm text-muted-foreground">Loading…</div>;
  }
  const isManagerOrAbove =
    user?.role === 'super_admin' ||
    ['owner', 'admin', 'manager'].includes(workspaceRole);
  if (!isManagerOrAbove) {
    return <GuardedRedirect label="manager-or-above" requiredRole="Manager or above" />;
  }
  return children;
}

function RoleBasedLayout() {
  const { workspaceRole, loading: wsLoading } = useWorkspace();
  const isClient = workspaceRole === 'client';

  if (wsLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <img src="/stp-logo.png" alt="Stellar" className="h-12 w-12 object-contain animate-pulse" />
          <p className="text-sm text-muted-foreground">Loading workspace...</p>
        </div>
      </div>
    );
  }

  if (isClient) return <ClientPortal />;
  return <AppShell />;
}

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) {
    return <StellarLoadingScreen />;
  }

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/unsubscribe" element={<UnsubscribePage />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      <Route path="/shared/project/:shareToken" element={<SharedProjectPage />} />
      <Route path="/share/post/:token" element={<PublicContentReviewPage />} />
      <Route path="/share/calendar/:token" element={<PublicCalendarReviewPage />} />

      {/* Phase 2 Group C3 — legacy/external bookmark redirect: /chat → /tasks/chat */}
      <Route path="/chat" element={<Navigate to="/tasks/chat" replace />} />
      {/* Phase 3A Live Fix #5 — same idea for bare /contracts → owner-gated route */}
      <Route path="/contracts" element={<Navigate to="/tasks/contracts" replace />} />

      {/* Root — show the App Chooser (Social Media CRM vs Task Manager).
          D85: un-hidden 2026-06-01 (previously hard-redirected to /tasks).
          Each chooser tile sets localStorage.stellar_active_tool and routes
          on into the respective portal. */}
      <Route path="/" element={
        <ProtectedRoute>
          <WorkspaceProvider>
            <AppSelectorPage />
          </WorkspaceProvider>
        </ProtectedRoute>
      } />

      {/* Social Media CRM routes — D85: un-hidden 2026-06-01.
          Pages are mounted under RoleBasedLayout → AppShell (CRM sidebar).
          Task Manager routes below remain byte-for-byte unchanged. */}
      <Route
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <RoleBasedLayout />
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      >
        <Route path="/crm" element={<DashboardPage />} />
        <Route path="/media" element={<MediaLibraryPage />} />
        <Route path="/calendar" element={<ContentCalendarPage />} />
        <Route path="/compose" element={<PostComposerPage />} />
        <Route path="/queue" element={<SocialQueuePage />} />
        <Route path="/clients/:clientGroupId/social-timeline" element={<ClientSocialTimelinePage />} />
        <Route path="/clients/:clientGroupId/content-calendar" element={<ClientContentCalendarPage />} />
        <Route path="/approvals" element={<ApprovalsPage />} />
        <Route path="/groups" element={<ClientGroupsPage />} />
        <Route path="/clients" element={<ClientDashboardPage />} />
        <Route path="/integrations" element={<IntegrationsPage />} />
        <Route path="/analytics" element={<AnalyticsPage />} />
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/ads" element={<AdManagerPage />} />
        <Route path="/ai" element={<AIAssistantPage />} />
        <Route path="/team" element={<TeamPage />} />
        <Route path="/admin" element={<AdminDashboardPage />} />
        {/* `/settings` is owned by Task Manager (UserSettingsPage). The legacy
            CRM SettingsPage is mounted at /crm/settings to avoid collision. */}
        <Route path="/crm/settings" element={<SettingsPage />} />
      </Route>

      {/* Task Manager routes */}
      <Route
        element={
          <ProtectedRoute>
            <WorkspaceProvider>
              <TaskManagerShell />
            </WorkspaceProvider>
          </ProtectedRoute>
        }
      >
        {/* BUG-D01 / D-001 — `/dashboard` is the canonical Dashboard URL.
            `/tasks` aliases to it for back-compat (was the original path). */}
        <Route path="/dashboard" element={<TaskDashboardPage />} />
        <Route path="/tasks" element={<Navigate to="/dashboard" replace />} />
        <Route path="/tasks/all" element={<UnifiedTasksPage />} />
        <Route path="/tasks/idea-bank" element={<IdeaBankPage />} />
        <Route path="/tasks/my" element={<Navigate to="/tasks/all" replace />} />
        <Route path="/tasks/team-tasks" element={<Navigate to="/tasks/all" replace />} />
        <Route path="/tasks/list" element={<Navigate to="/tasks/all" replace />} />
        <Route path="/tasks/board" element={<Navigate to="/tasks/all" replace />} />
        {/* BUG-T-002 / T-003 / T-005 / T-008 (2026-05-21) — Each Tasks tab
            now has a unique deep-linkable URL. UnifiedTasksPage reads the
            pathname on mount and pre-selects the matching tab; clicking
            a tab pushes the corresponding route so the browser back/forward
            buttons + copy-link sharing work. */}
        <Route path="/tasks/my-tasks" element={<UnifiedTasksPage />} />
        <Route path="/tasks/team" element={<UnifiedTasksPage />} />
        <Route path="/tasks/by-client" element={<UnifiedTasksPage />} />
        <Route path="/tasks/by-status" element={<UnifiedTasksPage />} />
        <Route path="/tasks/following" element={<UnifiedTasksPage />} />
        <Route path="/tasks/projects" element={<ProjectsPage />} />
        <Route path="/tasks/pages" element={<PagesPage />} />
        <Route path="/tasks/calendar" element={<TaskCalendarPage />} />
        <Route path="/tasks/notifications" element={<NotificationsPage />} />
        <Route path="/tasks/install-guide" element={<InstallGuidePage />} />
        {/* Batch 5 D48 BUG-09 — Reviews queue */}
        <Route path="/reviews" element={<ReviewsPage />} />
        <Route path="/tasks/reviews" element={<Navigate to="/reviews" replace />} />
        <Route path="/tasks/time" element={<TimeTrackingPage />} />
        <Route path="/tasks/permissions" element={<PermissionsPage />} />
        <Route path="/tasks/ai" element={<TaskAIPage />} />
        <Route path="/tasks/notes" element={<NotesPage />} />
        <Route path="/meeting-notes" element={<MeetingNotesPage />} />
        <Route path="/tasks/team-view" element={<TeamViewPage />} />
        <Route path="/tasks/team-manage" element={<Navigate to="/tasks/all" replace />} />
        <Route path="/tasks/team-tasks" element={<Navigate to="/tasks/all" replace />} />
        <Route path="/tasks/team" element={<Navigate to="/tasks/all" replace />} />
        <Route path="/tasks/report" element={<TeamReportPage />} />
        <Route path="/tasks/availability" element={<ErrorBoundary><TeamAvailabilityPage /></ErrorBoundary>} />
        <Route path="/tasks/attendance" element={<ErrorBoundary><AttendanceV2Page /></ErrorBoundary>} />
        <Route path="/attendance" element={<ErrorBoundary><AttendancePage /></ErrorBoundary>} />
        <Route path="/payroll" element={<ErrorBoundary><PayrollPage /></ErrorBoundary>} />
        <Route path="/tasks/clients" element={<MyClientsPage />} />
        <Route path="/tasks/integrations" element={<AdminOrOwnerRoute><IntegrationsHubPage /></AdminOrOwnerRoute>} />
        <Route path="/tasks/chat" element={<TeamChatPage />} />
        <Route path="/tasks/ai-chat" element={<AIChatPage />} />
        <Route path="/tasks/notion-import" element={<NotionImportPage />} />
        <Route path="/tasks/standup" element={<Navigate to="/tasks/report?tab=standup" replace />} />
        <Route path="/tasks/agents" element={<AdminOrOwnerRoute><AIAgentsPage /></AdminOrOwnerRoute>} />
        <Route path="/tasks/activity/:userId" element={<UserActivityPage />} />
        <Route path="/tasks/contracts" element={<OwnerOnlyRoute><ContractsPage /></OwnerOnlyRoute>} />
        <Route path="/tasks/contracts/templates" element={<OwnerOnlyRoute><ContractTemplatesPage /></OwnerOnlyRoute>} />
        <Route path="/tasks/contracts/templates/:templateId" element={<OwnerOnlyRoute><ContractTemplatesPage /></OwnerOnlyRoute>} />
        <Route path="/tasks/settings" element={<SettingsPage hideCredentials={true} />} />
        {/* Batch 2B C12 — /settings is the personal Settings page
            (Profile, Appearance, Notifications, Workspace shortcut).
            Lives inside the nested-Outlet block so TaskManagerShell wraps
            it correctly. */}
        <Route path="/settings" element={<UserSettingsPage />} />
        {/* Batch 5 D4-UI-2 — EOD Self submit + Team calendar */}
        <Route path="/eod" element={<ErrorBoundary><EodReportsPage /></ErrorBoundary>} />
        <Route path="/tasks/eod-report" element={<ErrorBoundary><EodReportsPage /></ErrorBoundary>} />
        {/* Batch 5 R3 F2 — Person Detail Page */}
        <Route path="/people/:user_id" element={<PersonDetailPage />} />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <SWUpdateToast />
          {/* Batch 2A D-022 — top-level ErrorBoundary so a single component
              crash on any social-media / CRM page degrades gracefully into a
              recoverable fallback instead of a blank screen. */}
          <ErrorBoundary label="this page">
            <AppRoutes />
          </ErrorBoundary>
        </AuthProvider>
      </BrowserRouter>
      <ThemedToaster />
    </ThemeProvider>
  );
}

function ThemedToaster() {
  const theme = localStorage.getItem('stellar_theme') || 'dark';
  // D53 — position-aware: bottom-center on mobile, bottom-right on desktop.
  // (CSS in index.css further pulls into safe-area-inset-bottom for iOS.)
  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches;
  return (
    <Toaster
      position={isMobile ? 'bottom-center' : 'bottom-right'}
      richColors
      theme={theme}
      toastOptions={{
        className: 'bg-card border border-border text-foreground',
      }}
    />
  );
}

export default App;
